import { useEffect, useRef } from 'react';
import * as bootstrap from 'bootstrap';

/**
 * Custom hook for tooltips that need to be interactive (clickable links, etc.)
 * @param {Object} options - Configuration options
 * @param {Array} dependencies - Array of dependencies that should trigger tooltip refresh
 */
export const useInteractiveTooltips = (options = {}, dependencies = []) => {
  const tooltipsRef = useRef([]);
  
  useEffect(() => {
    // Function to safely dispose tooltips
    const safeDispose = (tooltipInstance) => {
      if (!tooltipInstance) return;
      try {
        // Check if the element still exists before disposing
        if (tooltipInstance._element && document.body.contains(tooltipInstance._element)) {
          tooltipInstance.dispose();
        } else {
          // If element is gone, try to clean up the tooltip tip directly
          if (tooltipInstance.tip && tooltipInstance.tip.parentNode) {
            tooltipInstance.tip.parentNode.removeChild(tooltipInstance.tip);
          }
          
          // Clear any timeouts
          if (tooltipInstance._timeout) {
            clearTimeout(tooltipInstance._timeout);
          }
        }
      } catch (error) {
        console.warn('Error disposing tooltip:', error);
        // Try manual cleanup if standard disposal fails
        try {
          if (tooltipInstance.tip && tooltipInstance.tip.parentNode) {
            tooltipInstance.tip.parentNode.removeChild(tooltipInstance.tip);
          }
        } catch (e) {
          // Silently fail if even manual cleanup doesn't work
        }
      }
    };
    
    // Clean up existing tooltips
    tooltipsRef.current.forEach(safeDispose);
    tooltipsRef.current = [];
    
    // Initialize tooltips with interactive behavior
    const initInteractiveTooltips = () => {
      const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
      
      tooltipTriggerList.forEach(el => {
        try {
          // Check if element is in the document
          if (!document.body.contains(el)) return;
          
          // Remove any existing tooltip
          const existing = bootstrap.Tooltip.getInstance(el);
          if (existing) safeDispose(existing);
          
          // Create a standard Bootstrap tooltip with hover trigger
          const tooltip = new bootstrap.Tooltip(el, {
            html: true,
            sanitize: false,
            ...options
          });
          
          // Store the original element for cleanup
          tooltip._originalElement = el;
          
          // Make tooltips interactive by adding event listeners
          el.addEventListener('inserted.bs.tooltip', () => {
            if (tooltip.tip) {
              // Make tooltip stay open when hovering over it
              tooltip.tip.addEventListener('mouseenter', () => {
                // Prevent tooltip from hiding
                clearTimeout(tooltip._timeout);
                tooltip._hoverState = 'show';
              });
              
              tooltip.tip.addEventListener('mouseleave', () => {
                // Allow tooltip to hide after leaving
                tooltip._hoverState = 'out';
                tooltip._timeout = setTimeout(() => {
                  if (tooltip._hoverState === 'out') {
                    tooltip.hide();
                  }
                }, 100);
              });
              
              // Style links in tooltips
              const links = tooltip.tip.querySelectorAll('a');
              links.forEach(link => {
                link.style.color = '#007bff';
                link.style.textDecoration = 'underline';
                link.style.cursor = 'pointer';
              });
            }
          });
          
          tooltipsRef.current.push(tooltip);
        } catch (error) {
          console.warn('Error creating interactive tooltip:', error);
        }
      });
    };
    
    // Delay initialization to ensure DOM is ready
    const timeoutId = setTimeout(initInteractiveTooltips, 100);
    
    // Cleanup function
    return () => {
      clearTimeout(timeoutId);
      
      // Safely dispose all tooltips
      const tooltipsToDispose = [...tooltipsRef.current];
      tooltipsRef.current = [];
      
      // Use a try-catch block around the entire cleanup process
      try {
        tooltipsToDispose.forEach(tooltip => {
          try {
            // First try to hide the tooltip
            if (tooltip && typeof tooltip.hide === 'function') {
              tooltip.hide();
            }
            
            // Then try to dispose it
            if (tooltip) {
              // Check if the element still exists
              if (tooltip._element && document.body.contains(tooltip._element)) {
                tooltip.dispose();
              } else {
                // If element is gone, try to clean up the tooltip tip directly
                if (tooltip.tip && tooltip.tip.parentNode) {
                  tooltip.tip.parentNode.removeChild(tooltip.tip);
                }
              }
            }
          } catch (error) {
            // Silently fail individual tooltip cleanup
            // This prevents the error from being logged to the console
          }
        });
      } catch (error) {
        // Silently fail the entire cleanup process
        // This prevents the error from being logged to the console
      }
    };
  }, dependencies);
};
